/**
 * Design kits — the single source of truth for a generated app's visual identity.
 *
 * WHY KITS EXIST. Before this, every generated app inherited one stylesheet with a system
 * font stack and a single swappable accent, so two apps could differ only by hue. That is
 * structurally guaranteed sameness: the output was competent and templated, which is the
 * one thing a design tool cannot be.
 *
 * WHY THESE FONTS. Generated apps are self-contained and offline — the gate rejects
 * external stylesheets — so web fonts are out. That reads like a ceiling on typographic
 * personality until you notice iOS ships a genuinely characterful library with ZERO
 * download: Didot, Futura, Avenir Next, Iowan Old Style, American Typewriter, Optima,
 * ui-rounded, ui-monospace. Every face below is available on an iPhone with no network,
 * and each falls back through progressively more common families.
 *
 * Each kit is chosen FOR A SUBJECT, which is what keeps it from being a default. The model
 * picks by subject via `S.kit(id)`, and the gate REQUIRES that call — an app must make a
 * deliberate identity choice rather than fall into whatever the default happens to be.
 *
 * WHY THE GROUNDS CARRY COLOUR. Measured on 2026-09-26: every accent was already saturated
 * — 79%, 88%, 82% — and every background was grey, 0% to 5%. So each kit was one vivid dot
 * on an off-white sheet, and the sheet is most of what anybody sees. The accents did not
 * move; the paper did. `kitColour.test.ts` pins the floor, and names `gallery` as the one
 * deliberate exception: "so pictures do the talking" is its whole design, and a gallery that
 * tinted the wall would be a worse gallery.
 *
 * EVERY KIT SHIPS A DARK VARIANT. A fixed light palette meant five of six kits stayed
 * bright on a phone set to dark mode, which is not a design position so much as an
 * omission — a person who set their phone to dark did so on purpose. `dark` holds only the
 * tokens that change, applied under `prefers-color-scheme: dark`.
 *
 * Everything downstream (the stylesheet, the gate's valid-id list, the theme-color, the
 * prompt's documentation) derives from this array, so detector and runtime cannot drift.
 */
import { rnFontFamily } from './kitTokens';

export interface DesignKit {
  id: string;
  /** Shown to the PERSON choosing, in the wizard's Look card. */
  label: string;
  /** One line under the label — what it feels like, not what it is made of. */
  blurb: string;
  /** Shown to the model so it can choose by subject. */
  when: string;
  /** Drives <meta name="theme-color">, so the browser chrome matches the app. */
  accent: string;
  /** Light-mode tokens (the full set). */
  css: string;
  /** Only the tokens that change under prefers-color-scheme: dark. */
  dark: string;
}

export const KITS: DesignKit[] = [
  {
    id: 'ledger',
    label: 'Ledger',
    blurb: 'Cool blue paper and monospaced figures. Precise.',
    when: 'money, budgets, invoices, data, anything where precision and figures are the point',
    accent: '#2F5DE0',
    css: `
  color-scheme: light;
  --font-display: "Avenir Next", "Helvetica Neue", sans-serif;
  --font-body: -apple-system, "Helvetica Neue", sans-serif;
  --font-num: ui-monospace, "SF Mono", Menlo, monospace;
  --display-weight: 700; --display-track: -0.03em; --display-case: none;
  --bg: #EAF0FB; --surface: #FFFFFF; --surface-2: #D7E2F7;
  --ink: #0F172A; --ink-muted: #64748B; --line: rgba(15,23,42,0.10);
  --accent: #2F5DE0; --accent-ink: #FFFFFF; --accent-wash: rgba(47,93,224,0.08);
  --radius: 14px; --pad: 18px;
  --shadow: 0 1px 1px rgba(15,23,42,0.04), 0 8px 20px rgba(15,23,42,0.06);`,
    dark: `
  color-scheme: dark;
  --bg: #0B0F17; --surface: #141A24; --surface-2: #1E2634;
  --ink: #E6EDF7; --ink-muted: #8A97AA; --line: rgba(230,237,247,0.12);
  --accent: #6C93FF; --accent-ink: #08101F; --accent-wash: rgba(108,147,255,0.14);
  --shadow: 0 1px 1px rgba(0,0,0,0.5), 0 10px 24px rgba(0,0,0,0.4);`,
  },
  {
    id: 'editorial',
    label: 'Editorial',
    blurb: 'Didot headings on warm paper. Unhurried.',
    when: 'journals, reading, recipes, notes, anything written and read slowly',
    // Kept in step with `--accent` below — this one drives <meta name="theme-color">, so a
    // stale value here paints the browser chrome a colour the page no longer uses.
    accent: '#1F6B4A',
    css: `
  color-scheme: light;
  --font-display: Didot, "Bodoni 72", "Playfair Display", Georgia, serif;
  --font-body: "Iowan Old Style", Charter, Georgia, serif;
  --font-num: "Iowan Old Style", Georgia, serif;
  --display-weight: 400; --display-track: -0.01em; --display-case: none;
  --bg: #F6EFE2; --surface: #FFFBF3; --surface-2: #E8DCC4;
  --ink: #16150F; --ink-muted: #6B6857; --line: rgba(22,21,15,0.14);
  --accent: #1F6B4A; --accent-ink: #F7F6F1; --accent-wash: rgba(31,107,74,0.10);
  --radius: 4px; --pad: 20px;
  --shadow: none;`,
    dark: `
  color-scheme: dark;
  --bg: #14140F; --surface: #1C1C15; --surface-2: #262620;
  --ink: #EDE9DD; --ink-muted: #9C9683; --line: rgba(237,233,221,0.16);
  --accent: #8FC4AC; --accent-ink: #12160F; --accent-wash: rgba(143,196,172,0.12);`,
  },
  {
    id: 'sport',
    label: 'Sport',
    blurb: 'Condensed caps, electric blue. Built for momentum.',
    when: 'fitness, habits, streaks, goals — anything about effort and momentum',
    accent: '#1F4BFF',
    css: `
  color-scheme: light;
  --font-display: "Avenir Next Condensed", Futura, "Helvetica Neue", sans-serif;
  --font-body: "Avenir Next", -apple-system, sans-serif;
  --font-num: "Avenir Next Condensed", "Helvetica Neue", sans-serif;
  --display-weight: 700; --display-track: 0.02em; --display-case: uppercase;
  --bg: #EFF3FF; --surface: #FFFFFF; --surface-2: #D9E2FF;
  --ink: #0A0A0A; --ink-muted: #6E7278; --line: rgba(10,10,10,0.14);
  --accent: #1F4BFF; --accent-ink: #FFFFFF; --accent-wash: rgba(31,75,255,0.10);
  --radius: 10px; --pad: 18px;
  --shadow: none;`,
    dark: `
  color-scheme: dark;
  --bg: #0A0A0B; --surface: #16171A; --surface-2: #212328;
  --ink: #F4F5F7; --ink-muted: #9096A0; --line: rgba(244,245,247,0.14);
  --accent: #6C89FF; --accent-ink: #06060A; --accent-wash: rgba(108,137,255,0.14);`,
  },
  {
    id: 'soft',
    label: 'Soft',
    blurb: 'Rounded type, nothing sharp. Warm and encouraging.',
    when: 'wellness, food, family, social, anything warm and encouraging',
    accent: '#6D4AFF',
    css: `
  color-scheme: light;
  --font-display: ui-rounded, "SF Pro Rounded", "Avenir Next", sans-serif;
  --font-body: ui-rounded, "SF Pro Rounded", -apple-system, sans-serif;
  --font-num: ui-rounded, "SF Pro Rounded", -apple-system, sans-serif;
  --display-weight: 700; --display-track: -0.02em; --display-case: none;
  --bg: #F3ECFF; --surface: #FFFFFF; --surface-2: #E4D8FF;
  --ink: #241F33; --ink-muted: #7C7391; --line: rgba(36,31,51,0.09);
  --accent: #6D4AFF; --accent-ink: #FFFFFF; --accent-wash: rgba(109,74,255,0.10);
  --radius: 22px; --pad: 20px;
  --shadow: 0 2px 4px rgba(36,31,51,0.04), 0 12px 28px rgba(109,74,255,0.10);`,
    dark: `
  color-scheme: dark;
  --bg: #16121F; --surface: #211B2E; --surface-2: #2C2440;
  --ink: #EFEAF9; --ink-muted: #A79CC0; --line: rgba(239,234,249,0.12);
  --accent: #9B7BFF; --accent-ink: #140F1E; --accent-wash: rgba(155,123,255,0.16);
  --shadow: 0 2px 4px rgba(0,0,0,0.4), 0 12px 28px rgba(0,0,0,0.45);`,
  },
  {
    id: 'notebook',
    label: 'Notebook',
    blurb: 'Typewriter headings, jotted-down feel.',
    when: 'lists, plans, travel, collections, ideas — anything jotted down',
    accent: '#1F4E5F',
    css: `
  color-scheme: light;
  --font-display: "American Typewriter", "Courier New", monospace;
  --font-body: "Iowan Old Style", Charter, Georgia, serif;
  --font-num: "American Typewriter", "Courier New", monospace;
  --display-weight: 600; --display-track: -0.01em; --display-case: none;
  --bg: #FBF4E4; --surface: #FFFDF7; --surface-2: #EDE2C8;
  --ink: #1C1B18; --ink-muted: #736F63; --line: rgba(28,27,24,0.16);
  --accent: #1F4E5F; --accent-ink: #FFFFFF; --accent-wash: rgba(31,78,95,0.08);
  --radius: 6px; --pad: 18px;
  --shadow: 0 1px 0 rgba(28,27,24,0.06);`,
    dark: `
  color-scheme: dark;
  --bg: #14130F; --surface: #1D1C16; --surface-2: #272520;
  --ink: #EDE9DE; --ink-muted: #9A9484; --line: rgba(237,233,222,0.16);
  --accent: #78BACB; --accent-ink: #10171A; --accent-wash: rgba(120,186,203,0.12);`,
  },
  {
    id: 'arcade',
    label: 'Arcade',
    blurb: 'High contrast and playful. Made to be tapped.',
    when: 'games, puzzles, quizzes, scoreboards — anything played rather than used',
    accent: '#FF2E63',
    css: `
  color-scheme: light;
  --font-display: Futura, "Avenir Next", "Helvetica Neue", sans-serif;
  --font-body: "Avenir Next", -apple-system, sans-serif;
  --font-num: Futura, "Avenir Next", sans-serif;
  --display-weight: 700; --display-track: -0.01em; --display-case: uppercase;
  --bg: #FFF3E0; --surface: #FFFFFF; --surface-2: #FFDCC0;
  --ink: #17110C; --ink-muted: #7A6B5D; --line: rgba(23,17,12,0.16);
  --accent: #FF2E63; --accent-ink: #FFFFFF; --accent-wash: rgba(255,46,99,0.10);
  --radius: 18px; --pad: 18px;
  --shadow: 0 3px 0 rgba(23,17,12,0.14);`,
    dark: `
  color-scheme: dark;
  --bg: #140A12; --surface: #201320; --surface-2: #2C1A2B;
  --ink: #FDEFF6; --ink-muted: #B294A8; --line: rgba(253,239,246,0.14);
  --accent: #FF5C86; --accent-ink: #17070F; --accent-wash: rgba(255,92,134,0.16);
  --shadow: 0 3px 0 rgba(0,0,0,0.5);`,
  },
  {
    id: 'gallery',
    label: 'Gallery',
    blurb: 'Quiet and generous, so pictures do the talking.',
    when: 'photos, places, collections, anything browsed and looked at more than read',
    accent: '#111111',
    css: `
  color-scheme: light;
  --font-display: Optima, "Avenir Next", "Helvetica Neue", sans-serif;
  --font-body: -apple-system, "Helvetica Neue", sans-serif;
  --font-num: -apple-system, "Helvetica Neue", sans-serif;
  --display-weight: 400; --display-track: 0.01em; --display-case: none;
  --bg: #FDFCFB; --surface: #F5F4F2; --surface-2: #E9E7E4;
  --ink: #111111; --ink-muted: #8A8A8E; --line: rgba(17,17,17,0.10);
  --accent: #111111; --accent-ink: #FFFFFF; --accent-wash: rgba(17,17,17,0.06);
  --radius: 2px; --pad: 16px;
  --shadow: none;`,
    dark: `
  color-scheme: dark;
  --bg: #0B0B0C; --surface: #151517; --surface-2: #1F1F22;
  --ink: #F2F2F4; --ink-muted: #8E8E93; --line: rgba(242,242,244,0.12);
  --accent: #F2F2F4; --accent-ink: #0B0B0C; --accent-wash: rgba(242,242,244,0.08);`,
  },
  {
    id: 'console',
    label: 'Console',
    blurb: 'Dark and monospaced, like an instrument.',
    when: 'technical, developer, monitoring, utilities — anything that reads like an instrument',
    accent: '#E8A33D',
    css: `
  color-scheme: dark;
  --font-display: ui-monospace, "SF Mono", Menlo, monospace;
  --font-body: ui-monospace, "SF Mono", Menlo, monospace;
  --font-num: ui-monospace, "SF Mono", Menlo, monospace;
  --display-weight: 600; --display-track: -0.02em; --display-case: none;
  --bg: #0B1416; --surface: #142024; --surface-2: #1D2E33;
  --ink: #DCE3E1; --ink-muted: #7C8A88; --line: rgba(220,227,225,0.12);
  --accent: #E8A33D; --accent-ink: #0E1112; --accent-wash: rgba(232,163,61,0.12);
  --radius: 6px; --pad: 16px;
  --shadow: none;`,
    /* Already dark by design — an instrument reads as one in any setting. */
    dark: '',
  },
];

export const KIT_IDS = KITS.map((k) => k.id);

export function kitById(id: string | null | undefined): DesignKit | undefined {
  return KITS.find((k) => k.id === id);
}

/** The `:root[data-kit="…"]` blocks plus their dark-mode overrides. */
export function kitCss(): string {
  const light = KITS.map((k) => `:root[data-kit="${k.id}"] {${k.css}\n}`).join('\n');
  const dark = KITS.filter((k) => k.dark.trim())
    .map((k) => `  :root[data-kit="${k.id}"] {${k.dark}\n  }`)
    .join('\n');
  return `${light}\n@media (prefers-color-scheme: dark) {\n${dark}\n}`;
}

/** The handful of values a native swatch needs, parsed out of the token block.
 *
 *  Parsed rather than duplicated: `kits.ts` is the single source of truth for what an app
 *  looks like, and a hand-copied hex in the wizard would drift from the stylesheet the
 *  moment a kit is retuned — showing the user a preview of a design they will not get.
 */
export interface KitPreview {
  bg: string;
  surface: string;
  ink: string;
  inkMuted: string;
  accent: string;
  /** Radius in points, from `--radius`. */
  radius: number;
  /** `--font-display` as React Native should be given it (see `rnFontFamily`). */
  displayFont: string;
  uppercase: boolean;
}

function token(css: string, name: string): string {
  const m = css.match(new RegExp(`--${name}:\\s*([^;]+);`));
  return m ? m[1].trim() : '';
}

export function kitPreview(kit: DesignKit): KitPreview {
  /*
   * Through `rnFontFamily`, not `split(',')[0]`.
   *
   * Two kits lead their stack with a CSS generic — Soft with `ui-rounded`, Console with
   * `ui-monospace` — and several stacks name faces iOS cannot find by name. Whatever React
   * Native cannot resolve it draws in the plain system face, which is the one thing a card
   * whose whole job is to show a typeface cannot do. `rnFontFamily` owns that decision:
   * Menlo for Console, and `ui-rounded` itself for Soft, because that keyword is the only
   * name SF Rounded has on iOS and Fabric resolves it (see kitTokens.ts).
   */
  const family = rnFontFamily(token(kit.css, 'font-display'));
  return {
    bg: token(kit.css, 'bg') || '#FFFFFF',
    surface: token(kit.css, 'surface') || '#FFFFFF',
    ink: token(kit.css, 'ink') || '#111111',
    inkMuted: token(kit.css, 'ink-muted') || '#777777',
    accent: kit.accent,
    radius: parseInt(token(kit.css, 'radius'), 10) || 12,
    displayFont: family,
    uppercase: token(kit.css, 'display-case').toLowerCase() === 'uppercase',
  };
}

/** The kit an app chose, read from its source (`S.kit('editorial')`). */
export function kitOfSource(source: string | undefined): DesignKit | undefined {
  return kitById(/S\.kit\(\s*['"]([a-z-]+)['"]\s*\)/.exec(source ?? '')?.[1]);
}

/** The ink a kit puts ON its accent. Assuming white fails for a light accent — `console`'s
 *  amber wants near-black — and a mark for the app should be legible in its own colors. */
export function kitAccentInk(kit: DesignKit | undefined): string | undefined {
  return /--accent-ink:\s*(#[0-9A-Fa-f]{3,8})/.exec(kit?.css ?? '')?.[1];
}

/** A kit's corner radius, so a mark for the app can carry its shape as well as its color. */
export function kitRadius(kit: DesignKit | undefined): number | undefined {
  const px = /--radius:\s*(\d+)px/.exec(kit?.css ?? '')?.[1];
  return px ? Number(px) : undefined;
}
